import React, { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { PageHero, CTABanner } from "@/components/ui-blocks";

// 59 photos live in /public/gallery/phos-01.jpg ... phos-59.jpg
const PHOTOS = Array.from({ length: 59 }, (_, i) => {
  const n = String(i + 1).padStart(2, "0");
  return { src: `/gallery/phos-${n}.jpg`, alt: `PHOS Management project photo ${i + 1}` };
});

// Masonry column split (3 columns on lg)
const columnize = (arr, cols) => {
  const out = Array.from({ length: cols }, () => []);
  arr.forEach((item, i) => out[i % cols].push(item));
  return out;
};

export default function OurWork() {
  const [lightbox, setLightbox] = useState(null); // index
  const close = useCallback(() => setLightbox(null), []);
  const next = useCallback(() => setLightbox((i) => (i === null ? 0 : (i + 1) % PHOTOS.length)), []);
  const prev = useCallback(() => setLightbox((i) => (i === null ? 0 : (i - 1 + PHOTOS.length) % PHOTOS.length)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (lightbox === null) return;
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, close, next, prev]);

  const cols = columnize(PHOTOS, 3);
  const colsMd = columnize(PHOTOS, 2);

  return (
    <div data-testid="our-work-page">
      <PageHero
        eyebrow="Our Work"
        title={["Work that", "stands", "on its own."]}
        sub="A visual archive of PHOS Management projects — commercial builds, industrial expansions, multifamily renovations, roofing, siding, concrete, and residential remodels across Southeast Michigan."
      />

      {/* Photo count / meta */}
      <section className="relative border-y border-white/5 bg-obsidian-900/40">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-8">
            <div>
              <div className="font-display text-3xl leading-none text-accent">{PHOTOS.length}</div>
              <div className="overline mt-1">Photos</div>
            </div>
            <div className="hidden md:block h-8 w-px bg-white/10" />
            <div className="hidden md:block">
              <div className="font-display text-3xl leading-none">1,000+</div>
              <div className="overline mt-1">Projects Delivered</div>
            </div>
            <div className="hidden lg:block h-8 w-px bg-white/10" />
            <div className="hidden lg:block">
              <div className="font-display text-3xl leading-none">25+</div>
              <div className="overline mt-1">Years</div>
            </div>
          </div>
          <div className="overline text-ink-400">Tap any photo to enlarge</div>
        </div>
      </section>

      {/* Masonry gallery */}
      <section className="relative py-12 lg:py-20">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          {/* Desktop: 3 columns */}
          <div className="hidden lg:grid grid-cols-3 gap-4">
            {cols.map((col, ci) => (
              <div key={ci} className="flex flex-col gap-4">
                {col.map((p) => {
                  const idx = PHOTOS.indexOf(p);
                  return (
                    <GalleryItem key={p.src} idx={idx} photo={p} onClick={() => setLightbox(idx)} />
                  );
                })}
              </div>
            ))}
          </div>
          {/* Tablet: 2 columns */}
          <div className="hidden md:grid lg:hidden grid-cols-2 gap-4">
            {colsMd.map((col, ci) => (
              <div key={ci} className="flex flex-col gap-4">
                {col.map((p) => {
                  const idx = PHOTOS.indexOf(p);
                  return (
                    <GalleryItem key={p.src} idx={idx} photo={p} onClick={() => setLightbox(idx)} />
                  );
                })}
              </div>
            ))}
          </div>
          {/* Mobile: 1 column */}
          <div className="grid md:hidden grid-cols-1 gap-4">
            {PHOTOS.map((p, idx) => (
              <GalleryItem key={p.src} idx={idx} photo={p} onClick={() => setLightbox(idx)} />
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            data-testid="lightbox"
            className="fixed inset-0 z-[100] bg-obsidian-950/95 backdrop-blur-xl flex items-center justify-center p-4 lg:p-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={close}
          >
            <button
              data-testid="lightbox-close"
              onClick={(e) => { e.stopPropagation(); close(); }}
              className="absolute top-6 right-6 lg:top-8 lg:right-8 w-12 h-12 border border-white/20 flex items-center justify-center text-ink-50 hover:border-accent hover:text-accent"
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 w-12 h-12 border border-white/20 flex items-center justify-center text-ink-50 hover:border-accent hover:text-accent"
              aria-label="Previous"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 w-12 h-12 border border-white/20 flex items-center justify-center text-ink-50 hover:border-accent hover:text-accent"
              aria-label="Next"
            >
              <ChevronRight size={20} />
            </button>

            <motion.img
              key={lightbox}
              src={PHOTOS[lightbox].src}
              alt={PHOTOS[lightbox].alt}
              className="max-h-[85vh] max-w-[90vw] object-contain border border-white/10"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            />

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 overline text-ink-400">
              {String(lightbox + 1).padStart(2, "0")} / {String(PHOTOS.length).padStart(2, "0")}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <CTABanner eyebrow="Get a Quote" heading={<>Ready to start{"\n"}your project.</>} cta="Get a Quote" />
    </div>
  );
}

const GalleryItem = ({ idx, photo, onClick }) => (
  <motion.button
    onClick={onClick}
    data-testid={`gallery-item-${idx}`}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.7, delay: (idx % 9) * 0.04, ease: [0.22, 1, 0.36, 1] }}
    className="group relative block w-full text-left border border-white/10 hover:border-accent transition-colors duration-500 overflow-hidden"
  >
    <img
      src={photo.src}
      alt={photo.alt}
      loading="lazy"
      className="w-full h-auto block grayscale-hover group-hover:scale-[1.03] transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-obsidian-950/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
      <span className="overline text-ink-50 bg-obsidian-950/70 backdrop-blur px-3 py-1 border border-white/10">
        {String(idx + 1).padStart(2, "0")} · PHOS
      </span>
      <span className="w-9 h-9 border border-white/30 bg-obsidian-950/70 backdrop-blur flex items-center justify-center">
        <ArrowUpRight size={14} className="text-ink-50" />
      </span>
    </div>
  </motion.button>
);
